import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { httpClient } from "../../dependencies/utils/Api";
import {
  GET_APP_VERSION_ANDROID,
  UPDATE_APP_VERSION_ANDROID,
  GET_APP_VERSION_IOS,
  UPDATE_APP_VERSION_IOS,
  GET_STORE_VERSIONS,
} from "../../constants/ApplicationUrl";

const initialAndroid = {
  version: "",
  versionCode: "",
  minVersionCode: "",
  forceUpdate: false,
  showAlways: false,
  updateMessage: "A new version of the app is available. Please update now.",
};

const initialIos = {
  versionIos: "",
  versionCodeIos: "",
  minVersionCodeIos: "",
  forceUpdateIos: false,
  showAlwaysIos: false,
  updateMessageIos: "A new version of the app is available. Please update now.",
};

function AppVersion() {
  const [activeTab, setActiveTab] = useState("android");

  const [android, setAndroid] = useState(initialAndroid);
  const [ios, setIos] = useState(initialIos);

  const [androidHistory, setAndroidHistory] = useState([]);
  const [iosHistory, setIosHistory] = useState([]);

  const [loadingAndroid, setLoadingAndroid] = useState(false);
  const [loadingIos, setLoadingIos] = useState(false);
  const [savingAndroid, setSavingAndroid] = useState(false);
  const [savingIos, setSavingIos] = useState(false);

  const [liveStore, setLiveStore] = useState({ android: null, ios: null, fetchedAt: null });
  const [loadingStore, setLoadingStore] = useState(false);

  const fetchAndroid = async () => {
    setLoadingAndroid(true);
    try {
      const { data: res } = await httpClient.get(GET_APP_VERSION_ANDROID);
      const list = Array.isArray(res?.data) ? res.data : [];
      setAndroidHistory(list);
      const latest = list[0];
      if (latest) {
        setAndroid({
          version: latest.version || "",
          versionCode: latest.versionCode || "",
          minVersionCode: latest.minVersionCode || "",
          forceUpdate: Boolean(latest.forceUpdate),
          showAlways: Boolean(latest.showAlways),
          updateMessage: latest.updateMessage || initialAndroid.updateMessage,
        });
      }
    } catch (err) {
      toast.error("Failed to load Android version info.");
    } finally {
      setLoadingAndroid(false);
    }
  };

  const fetchIos = async () => {
    setLoadingIos(true);
    try {
      const { data: res } = await httpClient.get(GET_APP_VERSION_IOS);
      const list = Array.isArray(res?.data) ? res.data : [];
      setIosHistory(list);
      const latest = list[0];
      if (latest) {
        setIos({
          versionIos: latest.versionIos || "",
          versionCodeIos: latest.versionCodeIos || "",
          minVersionCodeIos: latest.minVersionCodeIos || "",
          forceUpdateIos: Boolean(latest.forceUpdateIos),
          showAlwaysIos: Boolean(latest.showAlwaysIos),
          updateMessageIos: latest.updateMessageIos || initialIos.updateMessageIos,
        });
      }
    } catch (err) {
      toast.error("Failed to load iOS version info.");
    } finally {
      setLoadingIos(false);
    }
  };

  const fetchLiveStoreVersions = async () => {
    setLoadingStore(true);
    try {
      const { data: res } = await httpClient.get(GET_STORE_VERSIONS);
      if (res?.status) {
        setLiveStore({
          android: res.data?.android || null,
          ios: res.data?.ios || null,
          fetchedAt: res.data?.fetchedAt || null,
        });
      }
    } catch (err) {
      toast.error("Failed to fetch live store versions.");
    } finally {
      setLoadingStore(false);
    }
  };

  useEffect(() => {
    fetchAndroid();
    fetchIos();
    fetchLiveStoreVersions();
  }, []);

  const applyStoreVersion = (platform) => {
    if (platform === "android") {
      const live = liveStore.android;
      if (!live?.success || !live.version) {
        toast.error("No live Android version available.");
        return;
      }
      const numericCode = live.version.replace(/\D/g, "") || android.versionCode;
      setAndroid({
        ...android,
        version: live.version,
        versionCode: numericCode,
      });
      toast.success(`Filled with Play Store version ${live.version}`);
    } else {
      const live = liveStore.ios;
      if (!live?.success || !live.version) {
        toast.error("No live iOS version available.");
        return;
      }
      const numericCode = live.version.replace(/\D/g, "") || ios.versionCodeIos;
      setIos({
        ...ios,
        versionIos: live.version,
        versionCodeIos: numericCode,
      });
      toast.success(`Filled with App Store version ${live.version}`);
    }
  };

  const validateAndroid = () => {
    if (!android.version.trim()) return "Android version name is required.";
    if (!android.versionCode.trim()) return "Android version code is required.";
    if (!/^\d+$/.test(android.versionCode)) return "Android version code must be numeric.";
    if (android.minVersionCode && !/^\d+$/.test(android.minVersionCode))
      return "Android minimum version code must be numeric.";
    if (android.minVersionCode && parseInt(android.minVersionCode, 10) > parseInt(android.versionCode, 10))
      return "Minimum version code cannot be greater than current version code.";
    return null;
  };

  const validateIos = () => {
    if (!ios.versionIos.trim()) return "iOS version name is required.";
    if (!ios.versionCodeIos.trim()) return "iOS version code is required.";
    if (!/^\d+$/.test(ios.versionCodeIos)) return "iOS version code must be numeric.";
    if (ios.minVersionCodeIos && !/^\d+$/.test(ios.minVersionCodeIos))
      return "iOS minimum version code must be numeric.";
    if (ios.minVersionCodeIos && parseInt(ios.minVersionCodeIos, 10) > parseInt(ios.versionCodeIos, 10))
      return "Minimum version code cannot be greater than current version code.";
    return null;
  };

  const saveAndroid = async (e) => {
    e.preventDefault();
    const err = validateAndroid();
    if (err) {
      toast.error(err);
      return;
    }
    if (android.forceUpdate) {
      const ok = window.confirm(
        "FORCE UPDATE is ON. All Android users below the latest version will be BLOCKED until they update.\n\nContinue?"
      );
      if (!ok) return;
    }
    setSavingAndroid(true);
    try {
      const payload = {
        version: android.version.trim(),
        versionCode: android.versionCode.trim(),
        minVersionCode: android.minVersionCode.trim() || "0",
        forceUpdate: android.forceUpdate,
        showAlways: android.showAlways,
        updateMessage: android.updateMessage.trim() || initialAndroid.updateMessage,
      };
      const { data: res } = await httpClient.post(UPDATE_APP_VERSION_ANDROID, payload);
      if (res?.status) {
        toast.success("Android version released.");
        fetchAndroid();
      } else {
        toast.error(res?.message || "Failed to release Android version.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to release Android version.");
    } finally {
      setSavingAndroid(false);
    }
  };

  const saveIos = async (e) => {
    e.preventDefault();
    const err = validateIos();
    if (err) {
      toast.error(err);
      return;
    }
    if (ios.forceUpdateIos) {
      const ok = window.confirm(
        "FORCE UPDATE is ON. All iOS users below the latest version will be BLOCKED until they update.\n\nContinue?"
      );
      if (!ok) return;
    }
    setSavingIos(true);
    try {
      const payload = {
        versionIos: ios.versionIos.trim(),
        versionCodeIos: ios.versionCodeIos.trim(),
        minVersionCodeIos: ios.minVersionCodeIos.trim() || "0",
        forceUpdateIos: ios.forceUpdateIos,
        showAlwaysIos: ios.showAlwaysIos,
        updateMessageIos: ios.updateMessageIos.trim() || initialIos.updateMessageIos,
      };
      const { data: res } = await httpClient.post(UPDATE_APP_VERSION_IOS, payload);
      if (res?.status) {
        toast.success("iOS version released.");
        fetchIos();
      } else {
        toast.error(res?.message || "Failed to release iOS version.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to release iOS version.");
    } finally {
      setSavingIos(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "—";
    const d = new Date(date);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const Badge = ({ color, children }) => (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        backgroundColor: color.bg,
        color: color.fg,
      }}
    >
      {children}
    </span>
  );

  const StatCard = ({ label, value, accent }) => (
    <div
      style={{
        flex: 1,
        background: "#fff",
        border: "1px solid #eef2f7",
        borderRadius: 10,
        padding: "16px 18px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        borderLeft: `4px solid ${accent}`,
      }}
    >
      <div style={{ fontSize: 12, color: "#8898aa", textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#32325d", marginTop: 4 }}>{value}</div>
    </div>
  );

  const renderForm = ({
    title,
    iconClass,
    iconColor,
    state,
    setState,
    fields,
    saving,
    loading,
    onSave,
    history,
  }) => {
    const latest = history[0];
    const releasesCount = history.length;
    const forceCount = history.filter((h) => h[fields.forceUpdate]).length;

    return (
      <>
        {/* Stat cards */}
        <div style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
          <StatCard label="Total Releases" value={releasesCount} accent={iconColor} />
          <StatCard label="Current Version" value={latest ? latest[fields.version] : "—"} accent="#1263AC" />
          <StatCard label="Current Code" value={latest ? latest[fields.versionCode] : "—"} accent="#28a745" />
          <StatCard label="Force-Update Releases" value={forceCount} accent="#fa1640" />
        </div>

        {/* Release form */}
        <div className="card-box">
          <h3 className="card-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <i className={iconClass} style={{ color: iconColor, fontSize: 20 }}></i>
            Release New {title} Version
          </h3>

          {loading ? (
            <div className="text-center p-4">Loading…</div>
          ) : (
            <form onSubmit={onSave}>
              <div className="row">
                <div className="col-md-6 mt-2">
                  <label className="focus-label">Latest Version (e.g. 4.1)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={state[fields.version]}
                    onChange={(e) => setState({ ...state, [fields.version]: e.target.value })}
                    placeholder="4.1"
                  />
                </div>
                <div className="col-md-6 mt-2">
                  <label className="focus-label">Latest Version Code (e.g. 41)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={state[fields.versionCode]}
                    onChange={(e) => setState({ ...state, [fields.versionCode]: e.target.value })}
                    placeholder="41"
                  />
                </div>
                <div className="col-md-6 mt-2">
                  <label className="focus-label">Minimum Supported Version Code</label>
                  <input
                    type="number"
                    className="form-control"
                    value={state[fields.minVersionCode]}
                    onChange={(e) => setState({ ...state, [fields.minVersionCode]: e.target.value })}
                    placeholder="0 = no minimum"
                  />
                  <small className="text-muted">
                    Users below this version code will be blocked (force update).
                  </small>
                </div>
                <div className="col-md-6 mt-2">
                  <label className="focus-label">Force Update</label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      height: 40,
                      paddingLeft: 4,
                    }}
                  >
                    <label className="switch" style={{ marginRight: 12 }}>
                      <input
                        type="checkbox"
                        checked={state[fields.forceUpdate]}
                        onChange={(e) =>
                          setState({ ...state, [fields.forceUpdate]: e.target.checked })
                        }
                      />
                      <span className="slider round"></span>
                    </label>
                    <span style={{ fontWeight: 600, color: state[fields.forceUpdate] ? "#fa1640" : "#666" }}>
                      {state[fields.forceUpdate] ? "ON — users will be blocked" : "OFF — users can skip"}
                    </span>
                  </div>
                </div>
                <div className="col-md-12 mt-2">
                  <div
                    style={{
                      background: state[fields.showAlways] ? "#fff8e1" : "#f6f9fc",
                      border: state[fields.showAlways] ? "1px solid #ffd54f" : "1px solid #eef2f7",
                      borderRadius: 8,
                      padding: "12px 14px",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <label className="switch" style={{ marginBottom: 0 }}>
                      <input
                        type="checkbox"
                        checked={state[fields.showAlways]}
                        onChange={(e) =>
                          setState({ ...state, [fields.showAlways]: e.target.checked })
                        }
                      />
                      <span className="slider round"></span>
                    </label>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: state[fields.showAlways] ? "#f57c00" : "#525f7f",
                        }}
                      >
                        <i className="fas fa-bullhorn" style={{ marginRight: 6 }}></i>
                        Show Modal to ALL Users (bypass version check)
                      </div>
                      <small className="text-muted">
                        When ON, the popup appears for every user instantly — even users on the latest
                        version. Useful for testing or sending announcements. Turn OFF to use normal
                        version-based logic.
                      </small>
                    </div>
                  </div>
                </div>
                <div className="col-md-12 mt-2">
                  <label className="focus-label">Update Message (shown in app popup)</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={state[fields.updateMessage]}
                    onChange={(e) => setState({ ...state, [fields.updateMessage]: e.target.value })}
                    placeholder="What's new in this version…"
                  />
                </div>
              </div>
              <div className="text-center" style={{ marginTop: 20 }}>
                <button type="submit" className="btn btn-primary submit-btn" disabled={saving}>
                  {saving ? "Releasing…" : "Release New Version"}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Version history timeline */}
        <div className="card-box">
          <h3 className="card-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <i className="fas fa-history" style={{ color: iconColor, fontSize: 18 }}></i>
            {title} Version History
            <span style={{ fontSize: 13, color: "#8898aa", fontWeight: 400 }}>
              ({releasesCount} {releasesCount === 1 ? "release" : "releases"})
            </span>
          </h3>

          {loading ? (
            <div className="text-center p-4">Loading…</div>
          ) : history.length === 0 ? (
            <div className="text-center p-4 text-muted">No releases yet. Release your first version above.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="table table-hover" style={{ marginBottom: 0 }}>
                <thead style={{ backgroundColor: "#f6f9fc", color: "#525f7f" }}>
                  <tr>
                    <th style={{ width: 60 }}>#</th>
                    <th>Version</th>
                    <th>Code</th>
                    <th>Min. Code</th>
                    <th>Force</th>
                    <th>Show All</th>
                    <th>Message</th>
                    <th style={{ width: 170 }}>Released On</th>
                    <th style={{ width: 100 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row, idx) => {
                    const isCurrent = idx === 0;
                    return (
                      <tr
                        key={row._id || idx}
                        style={{
                          backgroundColor: isCurrent ? "#f0f8ff" : "transparent",
                        }}
                      >
                        <td style={{ fontWeight: 600, color: "#8898aa" }}>{history.length - idx}</td>
                        <td style={{ fontWeight: 700, color: "#1263AC" }}>{row[fields.version]}</td>
                        <td>{row[fields.versionCode]}</td>
                        <td>{row[fields.minVersionCode] || "—"}</td>
                        <td>
                          {row[fields.forceUpdate] ? (
                            <Badge color={{ bg: "#ffe5ea", fg: "#fa1640" }}>Force</Badge>
                          ) : (
                            <Badge color={{ bg: "#e9f6ee", fg: "#28a745" }}>Optional</Badge>
                          )}
                        </td>
                        <td>
                          {row[fields.showAlways] ? (
                            <Badge color={{ bg: "#fff8e1", fg: "#f57c00" }}>On</Badge>
                          ) : (
                            <Badge color={{ bg: "#eef2f7", fg: "#8898aa" }}>Off</Badge>
                          )}
                        </td>
                        <td
                          style={{
                            maxWidth: 260,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: "#525f7f",
                          }}
                          title={row[fields.updateMessage]}
                        >
                          {row[fields.updateMessage] || "—"}
                        </td>
                        <td style={{ color: "#8898aa", fontSize: 13 }}>{formatDate(row.createdAt)}</td>
                        <td>
                          {isCurrent ? (
                            <Badge color={{ bg: "#1263AC", fg: "#fff" }}>Current</Badge>
                          ) : (
                            <Badge color={{ bg: "#eef2f7", fg: "#8898aa" }}>Old</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <head>
        <title>App Version | Fever99</title>
      </head>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-sm-12">
              <h4 className="page-title">App Version Management</h4>
              <p className="text-muted">
                Release a new version, see release history, and toggle <b>Force Update</b> per release.
              </p>
            </div>
          </div>

          {/* Live Store Versions card */}
          <div
            style={{
              background: "linear-gradient(135deg, #1263AC 0%, #3DDC84 100%)",
              borderRadius: 12,
              padding: 22,
              marginBottom: 24,
              color: "#fff",
              boxShadow: "0 4px 14px rgba(18, 99, 172, 0.25)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 14,
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="fas fa-broadcast-tower"></i>
                  Live Store Versions
                </div>
                <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                  Auto-fetched from Play Store &amp; App Store
                  {liveStore.fetchedAt && ` · last checked ${formatDate(liveStore.fetchedAt)}`}
                </div>
              </div>
              <button
                onClick={fetchLiveStoreVersions}
                disabled={loadingStore}
                style={{
                  background: "rgba(255,255,255,0.18)",
                  border: "1px solid rgba(255,255,255,0.4)",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: 8,
                  cursor: loadingStore ? "wait" : "pointer",
                  fontWeight: 600,
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <i
                  className="fas fa-sync-alt"
                  style={{ animation: loadingStore ? "spin 1s linear infinite" : "none" }}
                ></i>
                {loadingStore ? "Refreshing…" : "Refresh"}
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
              {/* Android live card */}
              <div
                style={{
                  background: "rgba(255,255,255,0.95)",
                  borderRadius: 10,
                  padding: 18,
                  color: "#32325d",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <i className="fab fa-android" style={{ fontSize: 22, color: "#3DDC84" }}></i>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>Google Play Store</span>
                </div>
                {loadingStore ? (
                  <div style={{ color: "#8898aa" }}>Loading…</div>
                ) : liveStore.android?.success ? (
                  <>
                    <div style={{ fontSize: 32, fontWeight: 700, color: "#1263AC", lineHeight: 1.2 }}>
                      v{liveStore.android.version}
                    </div>
                    {liveStore.android.lastUpdated && (
                      <div style={{ fontSize: 12, color: "#8898aa", marginTop: 4 }}>
                        Updated: {liveStore.android.lastUpdated}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                      <button
                        onClick={() => {
                          setActiveTab("android");
                          applyStoreVersion("android");
                        }}
                        className="btn btn-sm btn-primary"
                        style={{ fontSize: 12 }}
                      >
                        <i className="fas fa-arrow-down" style={{ marginRight: 4 }}></i>
                        Use this version
                      </button>
                      <a
                        href={liveStore.android.storeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize: 12 }}
                      >
                        <i className="fas fa-external-link-alt" style={{ marginRight: 4 }}></i>
                        Open
                      </a>
                    </div>
                  </>
                ) : (
                  <div style={{ color: "#fa1640", fontSize: 13 }}>
                    <i className="fas fa-exclamation-triangle"></i>{" "}
                    {liveStore.android?.error || "Could not fetch."}
                  </div>
                )}
              </div>

              {/* iOS live card */}
              <div
                style={{
                  background: "rgba(255,255,255,0.95)",
                  borderRadius: 10,
                  padding: 18,
                  color: "#32325d",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <i className="fab fa-apple" style={{ fontSize: 22, color: "#000" }}></i>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>Apple App Store</span>
                </div>
                {loadingStore ? (
                  <div style={{ color: "#8898aa" }}>Loading…</div>
                ) : liveStore.ios?.success ? (
                  <>
                    <div style={{ fontSize: 32, fontWeight: 700, color: "#1263AC", lineHeight: 1.2 }}>
                      v{liveStore.ios.version}
                    </div>
                    {liveStore.ios.releaseDate && (
                      <div style={{ fontSize: 12, color: "#8898aa", marginTop: 4 }}>
                        Released: {formatDate(liveStore.ios.releaseDate)}
                      </div>
                    )}
                    {liveStore.ios.minimumOsVersion && (
                      <div style={{ fontSize: 12, color: "#8898aa" }}>
                        Min iOS: {liveStore.ios.minimumOsVersion}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                      <button
                        onClick={() => {
                          setActiveTab("ios");
                          applyStoreVersion("ios");
                        }}
                        className="btn btn-sm btn-primary"
                        style={{ fontSize: 12 }}
                      >
                        <i className="fas fa-arrow-down" style={{ marginRight: 4 }}></i>
                        Use this version
                      </button>
                      <a
                        href={liveStore.ios.storeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize: 12 }}
                      >
                        <i className="fas fa-external-link-alt" style={{ marginRight: 4 }}></i>
                        Open
                      </a>
                    </div>
                  </>
                ) : (
                  <div style={{ color: "#fa1640", fontSize: 13 }}>
                    <i className="fas fa-exclamation-triangle"></i>{" "}
                    {liveStore.ios?.error || "Could not fetch."}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <ul
            className="nav nav-tabs"
            style={{ marginBottom: 20, borderBottom: "2px solid #eef2f7" }}
          >
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "android" ? "active" : ""}`}
                onClick={() => setActiveTab("android")}
                style={{
                  border: "none",
                  background: "transparent",
                  padding: "10px 22px",
                  fontWeight: 600,
                  color: activeTab === "android" ? "#3DDC84" : "#8898aa",
                  borderBottom: activeTab === "android" ? "3px solid #3DDC84" : "3px solid transparent",
                  marginBottom: -2,
                }}
              >
                <i className="fab fa-android" style={{ marginRight: 8 }}></i>
                Android
                <span
                  style={{
                    marginLeft: 8,
                    background: "#eef2f7",
                    padding: "2px 8px",
                    borderRadius: 10,
                    fontSize: 11,
                    color: "#525f7f",
                  }}
                >
                  {androidHistory.length}
                </span>
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "ios" ? "active" : ""}`}
                onClick={() => setActiveTab("ios")}
                style={{
                  border: "none",
                  background: "transparent",
                  padding: "10px 22px",
                  fontWeight: 600,
                  color: activeTab === "ios" ? "#000" : "#8898aa",
                  borderBottom: activeTab === "ios" ? "3px solid #000" : "3px solid transparent",
                  marginBottom: -2,
                }}
              >
                <i className="fab fa-apple" style={{ marginRight: 8 }}></i>
                iOS
                <span
                  style={{
                    marginLeft: 8,
                    background: "#eef2f7",
                    padding: "2px 8px",
                    borderRadius: 10,
                    fontSize: 11,
                    color: "#525f7f",
                  }}
                >
                  {iosHistory.length}
                </span>
              </button>
            </li>
          </ul>

          {activeTab === "android" &&
            renderForm({
              title: "Android",
              iconClass: "fab fa-android",
              iconColor: "#3DDC84",
              state: android,
              setState: setAndroid,
              fields: {
                version: "version",
                versionCode: "versionCode",
                minVersionCode: "minVersionCode",
                forceUpdate: "forceUpdate",
                showAlways: "showAlways",
                updateMessage: "updateMessage",
              },
              saving: savingAndroid,
              loading: loadingAndroid,
              onSave: saveAndroid,
              history: androidHistory,
            })}

          {activeTab === "ios" &&
            renderForm({
              title: "iOS",
              iconClass: "fab fa-apple",
              iconColor: "#000",
              state: ios,
              setState: setIos,
              fields: {
                version: "versionIos",
                versionCode: "versionCodeIos",
                minVersionCode: "minVersionCodeIos",
                forceUpdate: "forceUpdateIos",
                showAlways: "showAlwaysIos",
                updateMessage: "updateMessageIos",
              },
              saving: savingIos,
              loading: loadingIos,
              onSave: saveIos,
              history: iosHistory,
            })}
        </div>
      </div>

      <style>{`
        .switch { position: relative; display: inline-block; width: 50px; height: 24px; }
        .switch input { opacity: 0; width: 0; height: 0; }
        .slider {
          position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0;
          background-color: #ccc; transition: .3s; border-radius: 24px;
        }
        .slider:before {
          position: absolute; content: ""; height: 18px; width: 18px;
          left: 3px; bottom: 3px; background-color: white;
          transition: .3s; border-radius: 50%;
        }
        input:checked + .slider { background-color: #1263AC; }
        input:checked + .slider:before { transform: translateX(26px); }

        .table-hover tbody tr:hover { background-color: #f6f9fc !important; }
        .nav-link { cursor: pointer; }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}

export default AppVersion;
